// utils/getCandidateScoresTableData.js

import { getNominationRoundYear } from './getNominationRoundYear';
import { CM_PRIMARY_ID, SE_COMMITTEE_ID, cmSeLabel } from './constants';
import { calculateAverageScore } from './candidateScoreCalculations';

const getCommitteeName = (committeeId, sectionalCommitteesWithCmse) => {
  if (committeeId === CM_PRIMARY_ID || committeeId === SE_COMMITTEE_ID) {
    return cmSeLabel;
  }

  const committee = sectionalCommitteesWithCmse.find(
    (sc) => sc.id === committeeId,
  );
  return committee?.attributes?.name || '';
};

const getCandidateName = (candidateId, candidateArray) => {
  const candidate = candidateArray.find(
    (c) => (c.id || c.attributes?.id) === candidateId,
  );

  if (!candidate) return '';

  const { attributes } = candidate;
  const { name, lastName } = attributes;

  return `${name} ${lastName}`.trim() || '';
};

const getScorerDetails = (
  userId,
  committeeArray,
  fellowsArray,
  nonAasRefereesArray,
) => {
  for (const committee of committeeArray) {
    const chair = committee.attributes?.committeeChair?.data;
    if (chair && chair.id === userId) {
      return {
        name: chair.attributes.username?.split(' (')[0] || '',
        role: 'Chair',
        committee,
      };
    }

    const members = committee.attributes?.committeeMembers?.data || [];
    const member = members.find((m) => m.id === userId);
    if (member) {
      return {
        name: member.attributes.username?.split(' (')[0] || '',
        role: 'Member',
        committee,
      };
    }
  }

  const fellow = fellowsArray.find(
    (f) => (f.id || f.attributes?.id) === userId,
  );
  if (fellow) {
    const attributes = fellow.attributes || fellow;
    return {
      name:
        attributes.username?.split(' (')[0] ||
        `${attributes.firstName || ''} ${attributes.lastName || ''}`.trim() ||
        '',
      role: 'Fellow',
      committee: null,
    };
  }

  const referee = nonAasRefereesArray.find(
    (r) => (r.id || r.attributes?.id) === userId,
  );
  if (referee) {
    const attributes = referee.attributes || referee;
    return {
      name:
        attributes.username?.split(' (')[0] ||
        `${attributes.firstName || ''} ${attributes.lastName || ''}`.trim() ||
        '',
      role: 'Referee',
      committee: null,
    };
  }

  return {
    name: '',
    role: '',
    committee: null,
  };
};

const processScore = (
  score,
  candidateArray,
  committeeArray,
  sectionalCommitteesWithCmse,
  fellowsArray,
  nonAasRefereesArray,
  allFilteredScores,
  electionTypes,
  nominationStatusTypes,
) => {
  const attributes = score.attributes || score;
  const candidateName = getCandidateName(
    attributes.candidateId,
    candidateArray,
  );
  const scorerDetails = getScorerDetails(
    attributes.userId,
    committeeArray,
    fellowsArray,
    nonAasRefereesArray,
  );

  let committeeName = '';
  if (attributes.sectionalCommitteeId) {
    committeeName = getCommitteeName(
      attributes.sectionalCommitteeId,
      sectionalCommitteesWithCmse,
    );
  } else if (scorerDetails.committee) {
    committeeName = getCommitteeName(
      scorerDetails.committee.id,
      sectionalCommitteesWithCmse,
    );
  } else {
    committeeName = '';
  }

  const averageScore = calculateAverageScore(
    attributes.candidateId,
    allFilteredScores,
  );

  return {
    candidateName,
    committeeName,
    nominationStatusType:
      nominationStatusTypes?.[attributes?.nominationStatusTypeId] || '',
    scorerName: scorerDetails.name,
    scorerRole: scorerDetails.role,
    scientificExcellence: attributes.scientificExcellence,
    internationalProfile: attributes.internationalProfile,
    connectionToAustralian: attributes.connectionToAustralian,
    scientificLeadership: attributes.scientificLeadership,
    nationalProfile: attributes.nationalProfile,
    leadershipMentorship: attributes.leadershipMentorship,
    serviceToScience: attributes.serviceToScience,
    academyVision: attributes.academyVision,
    totalScore: attributes.totalScore,
    averageScore,
    comments: attributes.comments || '',
    createdAt: attributes.createdAt,
    updatedAt: attributes.updatedAt,
    publishedAt: attributes.publishedAt,

    candidateId: attributes.candidateId,
    userId: attributes.userId,
    sectionalCommitteeId: attributes.sectionalCommitteeId,
    electionTypeId: attributes.electionTypeId,
    electionType: electionTypes[attributes.electionTypeId]
      ? electionTypes[attributes.electionTypeId]
      : '',
  };
};

export const getCandidateScoresTableData = (
  selectedCommittee,
  candidateScoresArray,
  committeeArray,
  sectionalCommitteesWithCmse,
  fellowsArray,
  nonAasRefereesArray,
  nominationArray,
  candidateArray,
  electionTypes,
  nominationStatusTypes,
  selectedElectionType = 'all',
) => {
  const currentRound = getNominationRoundYear(new Date());

  let filteredScores = candidateScoresArray?.filter((score) => {
    const attributes = score.attributes || score;
    const nominationRound = String(attributes.nominationRound);
    return nominationRound === String(currentRound);
  });

  if (selectedElectionType !== 'all') {
    const electionTypeId = parseInt(selectedElectionType);
    filteredScores = filteredScores.filter((score) => {
      const attributes = score.attributes || score;
      return attributes.electionTypeId === electionTypeId;
    });
  }

  if (selectedCommittee) {
    const selectedCommitteeId = parseInt(selectedCommittee);

    filteredScores = filteredScores.filter((score) => {
      const attributes = score.attributes || score;

      if (attributes.sectionalCommitteeId) {
        return attributes.sectionalCommitteeId === selectedCommitteeId;
      }

      const scorerDetails = getScorerDetails(
        attributes.userId,
        committeeArray,
        fellowsArray,
        nonAasRefereesArray,
      );

      return scorerDetails.committee?.id === selectedCommitteeId;
    });
  }

  const allTableData = filteredScores.map((score) => {
    const nId = score.attributes.nominationId;

    const nomination = nominationArray?.find(
      (n) => String(n.id) === String(nId),
    );

    const nominationStatusTypeId =
      nomination?.attributes?.nominationStatusTypeId;

    const updatedScore = {
      ...score,
      attributes: {
        ...score.attributes,
        nominationStatusTypeId,
      },
    };

    return processScore(
      updatedScore,
      candidateArray,
      committeeArray,
      sectionalCommitteesWithCmse,
      fellowsArray,
      nonAasRefereesArray,
      filteredScores,
      electionTypes,
      nominationStatusTypes,
    );
  });

  allTableData.sort((a, b) => {
    const candidateCompare = a.candidateName.localeCompare(b.candidateName);
    if (candidateCompare !== 0) return candidateCompare;
    return a.scorerName.localeCompare(b.scorerName);
  });

  return {
    allTableData,
    totalScores: allTableData.length,
    completedScores: allTableData.filter((row) => row.isCompleted).length,
  };
};
